import type { CanvasTarget } from '../../types';

export function appPreviewTarget(
  toolName: string,
  output: unknown,
  threadId: string,
): CanvasTarget | null {
  if (!['app_preview', 'app_open'].includes(toolName) || !output || typeof output !== 'object')
    return null;
  const value = (output as { appPreview?: unknown }).appPreview;
  if (!value || typeof value !== 'object') return null;
  const preview = value as { id?: unknown; title?: unknown; threadId?: unknown; kind?: unknown };
  if (
    typeof preview.id !== 'string' ||
    !/^[a-zA-Z0-9_-]{1,128}$/.test(preview.id) ||
    typeof preview.title !== 'string' ||
    preview.title.length > 200 ||
    preview.threadId !== threadId
  )
    return null;
  const appKind = preview.kind === 'app' || preview.kind === 'deck' ? preview.kind : undefined;
  return appKind
    ? { kind: 'app-preview', id: preview.id, title: preview.title, appKind }
    : { kind: 'app-preview', id: preview.id, title: preview.title };
}

/** A preview whose page loaded but never drew the App (src/apps/tracer/collection-probe-result.mjs app_not_mounted):
 * its entry exports a component without mounting it. The result names no preview; the thread it is in is its own. */
export function appNotMounted(toolName: string | undefined, output: unknown): boolean {
  const value = output as { status?: unknown; error?: unknown } | null;
  return (
    toolName === 'app_preview' && value?.status === 'error' && value.error === 'app_not_mounted'
  );
}

/** The App sandbox tools (src/apps/tracer/tools.mjs): the moment a chat calls one, it is building an App. */
export const APP_SANDBOX_TOOLS: ReadonlySet<string> = new Set([
  'app_list_files',
  'app_read_file',
  'app_write_file',
  'app_run',
  'app_build',
  'app_add_packages',
  'app_preview',
  'app_screenshot',
  'app_open',
]);
/** Tools that can start the App canvas automatically. Read-only inspection stays in chat. */
export const APP_AUTO_OPEN_TOOLS: ReadonlySet<string> = new Set([
  'app_write_file',
  'app_run',
  'app_build',
  'app_preview',
  'app_screenshot',
]);
/** The App canvas of a chat that is building its first App: the build state, until a build can be shown. */
export const APP_BUILDING_ID = 'building';

/** The live snapshot of the built App that a build or command handed the canvas (`livePreview`), for this conversation. */
export function liveAppPreviewTarget(
  toolName: string | undefined,
  output: unknown,
  threadId: string,
): CanvasTarget | null {
  if ((toolName !== 'app_build' && toolName !== 'app_run') || !output || typeof output !== 'object')
    return null;
  const livePreview = (output as { livePreview?: unknown }).livePreview;
  return appPreviewTarget('app_preview', { appPreview: livePreview }, threadId);
}

/**
 * How a finished App preview reaches the canvas (operator 08:15): the FIRST preview of a thread opens the App at once,
 * from a closed canvas or from Activity; a later preview updates the App in place when the App is on show, opens it
 * when the canvas is closed, and leaves a canvas the person chose (Activity or anything else) alone. Activity stays
 * selectable. `opened` is true once this thread has shown an App preview in this page session.
 */
export function appPreviewOpening(
  preview: CanvasTarget,
  state: { sameThread: boolean; canvas: CanvasTarget | null; opened: boolean },
): {
  target: CanvasTarget;
  options: { auto: boolean; replace: boolean; fromActivity?: boolean };
} | null {
  const { canvas } = state;
  if (state.sameThread && canvas?.kind === 'app-preview')
    return {
      target: { ...preview, full: canvas.full },
      options: { auto: false, replace: true },
    };
  if (canvas === null || (canvas.kind === 'activity' && !state.opened))
    return {
      target: preview,
      options: {
        auto: true,
        replace: false,
        ...(canvas?.kind === 'activity' ? { fromActivity: true } : {}),
      },
    };
  // Another canvas the person chose stays; auto-open keeps its usual guard.
  return { target: preview, options: { auto: true, replace: false } };
}
